import React, { useState, useRef, useEffect } from 'react'
import { NavLink } from 'react-router-dom'
import {
  Button,
  MenuItem,
  ClickAwayListener,
  Grow,
  Paper,
  Popper,
  MenuList,
} from '@material-ui/core'

import { access, ROLE } from '../../../../security'

import { DownArrowIcon } from '../../../../uiKit/icons/Icons'

import { getAccountSettings, updateAccountSettings } from '../../api/account'
import { loadBotAccessors } from '../../../settings/api/settings'
import {logout as sendLogout, logoutAuth0User, logoutAuth0QcsHandoffUser, logoutQcsHandoffUser} from '../../../authorization/api/admin'
import { isQcsHandoffSession } from '../../../authorization/helpers/qcsHandoffSession'
import {PrimaryColorSwitch} from '../../../../uiKit/Switch'

import { hasPermissionForFeatureInAnyBot } from '../../../../security'

const imgContainerStyles = {
  position: 'relative',
}

const statusStyles = {
  position: 'absolute',
  boxSizing: 'border-box',
  width: 10,
  height: 10,
  border: '2px solid var(--color-white)',
  borderRadius: '50%',
  bottom: 0,
}

const baseImage = '/images/platform/default-user-icon.svg'

// Same silhouette as the static default-user-icon.svg asset, but drawn inline
// so its fill can follow --careco-primary on QCS surfaces (minimal menu).
// CareBrain keeps the baked-in asset so the avatar colour is unchanged.
const DefaultUserIcon = ({ className }) => (
  <svg
    className={className}
    width="32"
    height="32"
    viewBox="0 0 37 38"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true">
    <path
      d="M29.9708 25.6069L24.4304 24.4973C23.8426 24.3808 23.4145 23.8589 23.4145 23.256V21.9843C23.7793 21.4776 24.1213 20.8012 24.476 20.0944C24.747 19.5599 25.1549 18.7543 25.3373 18.5643C26.4114 17.5129 27.5565 16.2083 27.9061 14.5084C28.2228 12.9681 27.9061 12.1676 27.5388 11.5343C27.5388 10.2955 27.5388 8.02813 27.1081 6.39413C27.065 4.42067 26.7028 3.306 25.7933 2.31293C25.1498 1.6188 24.2024 1.45667 23.4424 1.32747C23.1409 1.2768 22.728 1.20587 22.5836 1.12733C21.2688 0.400267 19.9337 0.0405333 18.2997 0C14.9557 0.126667 10.8517 2.24707 9.47864 6.05973C9.06064 7.21747 9.10117 9.11747 9.13157 10.6451L9.0961 11.5343C8.77437 12.1676 8.4577 12.9681 8.7769 14.5059C9.1265 16.2133 10.2716 17.5155 11.3406 18.5617C11.5256 18.7543 11.9385 19.5649 12.2121 20.0995C12.5718 20.8037 12.9164 21.4801 13.2812 21.9868V23.256C13.2812 23.8589 12.853 24.3808 12.2628 24.4973L6.72237 25.6069C3.1833 26.3137 0.614502 29.4475 0.614502 33.0575V35.4667C0.614502 36.8625 1.75197 38 3.14784 38H33.5478C34.9437 38 36.0812 36.8625 36.0812 35.4667V33.0575C36.0812 29.4475 33.5124 26.3137 29.9708 25.6069Z"
      fill="var(--careco-primary)"
    />
  </svg>
)

export const UserMenu = props => {
  const [open, setOpen] = useState(false)
  const currentAdminId = props.adminUser && props.adminUser.id
  const anchorRef = useRef(null)
  const getImage = props?.accountSettings?.imageUrl || baseImage
  const activeColor = props?.accountSettings?.active ?
    'var(--color-malachite-green)' : 'var(--color-spun-pearl-grey)'
  const displayName =
    (props.accountSettings && props.accountSettings.name) ||
    (props.adminUser && props.adminUser.name)
  const isLoader = Boolean(currentAdminId)
  const ACCOUNT_SETTINGS = 'account-settings'
  const [hasPermissionForAdminSettings, setPermissionForAdminSettings] = useState(false)

  useEffect(() => {
    if (!currentAdminId) {
      access.loadRole()
      return
    }

    setPermissionForAdminSettings(
      hasPermissionForFeatureInAnyBot(ACCOUNT_SETTINGS, props.adminUser.botId),
    )

    getAccountSettings(currentAdminId)
  }, [currentAdminId])

  const handleToggle = () => {
    setOpen(!open)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleLogout = () => {
    if (isQcsHandoffSession()) {
      handleClose()
      if (props.adminUser?.isAuth0User) logoutAuth0QcsHandoffUser()
      else logoutQcsHandoffUser()
      return
    }
    if (props.adminUser?.isAuth0User) {
      handleClose()
      logoutAuth0User()
      return
    }
    sendLogout().then(() => {
      handleClose()
    })
  }

  const handleChangeStatus = () => {
    if (!props.accountSettings || !currentAdminId) return

    const isSupportAdmin = props.adminUser.botAccesses
      .find((bot) => bot.botId === Number(props.botId))?.role === ROLE.MANAGER

    updateAccountSettings(currentAdminId, {
      ...props.accountSettings,
      active: !props.accountSettings.active,
    }).then(() => {
      if (props.botId && !isSupportAdmin) {
        loadBotAccessors(props.botId)
      }
    })
  }

  function handleListKeyDown(event) {
    if (event.key === 'Tab') {
      event.preventDefault()
      setOpen(false)
    }
  }

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = useRef(open)

  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      // anchorRef.current.focus();
    }

    prevOpen.current = open
  }, [open])

  return (
    <>
      {isLoader && (
        <div className={props.classes.root}>
          <ClickAwayListener onClickAway={handleClose}>
            <>
              <Button
                ref={anchorRef}
                aria-controls={open ? 'menu-list-grow' : undefined}
                aria-haspopup="true"
                onClick={handleToggle}
                data-autotest={'logout-menu-popup-button'}
                className={props.classes.button}>
                <div style={imgContainerStyles}>
                  {getImage === baseImage && props.minimal ? (
                    <DefaultUserIcon className={props.classes.userImg} />
                  ) : (
                    <img
                      className={props.classes.userImg}
                      src={getImage}
                      alt="user photo"
                    />
                  )}
                  <span style={{ ...statusStyles, background: activeColor }} />
                </div>
                {displayName && <p>{displayName}</p>}
                <span
                  style={{
                    transform: open && 'rotate(180deg)',
                    marginLeft: '8px',
                  }}>
                  <DownArrowIcon />
                </span>
              </Button>
              <Popper
                transition
                disablePortal
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                placement="right-end"
                className={props.classes.popper}>
                {({ TransitionProps, placement }) => (
                  <Grow
                    {...TransitionProps}
                    style={{
                      transformOrigin:
                        placement === 'bottom' ? 'center top' : 'center bottom',
                    }}>
                    <Paper>
                      <MenuList
                        id="menu-list-grow"
                        onKeyDown={handleListKeyDown}
                        className={props.classes.list}>
                        {/*
                          The QCS organisations home passes `minimal` to strip
                          the menu down to Log out — availability and Account
                          Settings are not part of that surface.
                        */}
                        {!props.minimal && (
                          <MenuItem
                            onClick={handleChangeStatus}
                            className={props.classes.listItem}>
                            <span>I am available</span>
                            <PrimaryColorSwitch
                              checked={Boolean(props.accountSettings && props.accountSettings.active)}
                              color="primary"
                              name="checkedB"
                              inputProps={{ 'aria-label': 'primary checkbox' }}
                            />
                          </MenuItem>
                        )}
                        {!props.minimal && hasPermissionForAdminSettings &&
                        props.location.pathname !== '/account-settings' &&
                        props.redirectToAccountSettings && (
                          <MenuItem
                            onClick={props.redirectToAccountSettings}
                            className={props.classes.listItem}>
                            Account Settings
                          </MenuItem>
                        )}

                        {!props.minimal && hasPermissionForAdminSettings &&
                        props.location.pathname !== '/account-settings' &&
                        !props.redirectToAccountSettings && (
                          <MenuItem className={props.classes.listItem}>
                            <NavLink
                              to={{
                                pathname: '/account-settings',
                                state: { from: props.location.pathname },
                              }}>
                              Account Settings
                            </NavLink>
                          </MenuItem>
                        )}
                        <MenuItem
                          data-autotest={'logout-popup-item'}
                          onClick={handleLogout}
                          className={props.classes.listItem}>
                          Log out
                        </MenuItem>
                      </MenuList>
                    </Paper>
                  </Grow>
                )}
              </Popper>
            </>
          </ClickAwayListener>
        </div>
      )}
    </>
  )
}
